import React, { useState, useEffect, useRef } from "react";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useNavigate, useLocation } from "react-router-dom";

import "./SearchField.css";
import useUSUndo from "../../hooks/useUSUndo";
import useUSQSUndo from "../../hooks/useUSQSUndo";
import useSPUndo from "../../hooks/useSPUndo";
import useSPQSUndo from "../../hooks/useSPQSUndo";

/**
 * SearchField component.
 *
 * @param {Object} style - Inline styles to be applied to the input field.
 * @param {Object} sfliststyle - Inline styles for the search result list (dropdown).
 * @param {string} searchURL - The URL to fetch search data from.
 * @param {string} value - The current value to be displayed in the input field.
 * @param {string} valueName - The key of the value to display in the search list from each item in the data.
 * @param {Function} onItemSelect - Callback function to be executed when an item is selected from the search list.
 * @param {string} valueID - The key of the unique identifier for each item in the search list.
 * @param {boolean} required - Indicates whether the input field is required.
 * @param {boolean} clearfield - If true, clears the input field when this flag changes.
 * @param {string} secondaryValueName - An optional secondary value to display in the search list (e.g., name and description).
 * @param {string} customplaceholder - Custom placeholder text for the input field.
 */

const SearchField = ({
  style,
  sfliststyle,
  searchURL,
  value,
  valueName,
  onItemSelect,
  valueID,
  required,
  clearfield,
  secondaryValueName,
  customplaceholder,
  refName,
}) => {
  // General
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const location = useLocation();

  // console.log(location);

  const [searchlistdata, setSearchlistdata] = useState();
  const [showsflist, setShowsflist] = useState(false);
  const [searchtext, setSearchtext] = useState(null);
  const [filteredSFdata, setFilteredSFdata] = useState();

  const [editingSF, setEditingSF] = useState(false);

  let SFRefs = useRef([]);
  let SFCurrentIndex;

  const { inputRefs } = useUSUndo();
  const { inputRefsUSQS } = useUSQSUndo();
  const { inputRefsSP } = useSPUndo();
  const { inputRefsSPQS } = useSPQSUndo();

  // Load Data
  const loaddata = () => {
    try {
      axiosPrivate.get(searchURL).then((response) => {
        setSearchlistdata(response.data);
      });
    } catch (err) {
      navigate("/login", { state: { from: location }, replace: true });
    }
  };

  // Filter
  useEffect(() => {
    if (searchlistdata && searchtext) {
      let result;
      if (secondaryValueName) {
        result = searchlistdata.filter(
          (item) =>
            item[valueName].toLowerCase().includes(searchtext.toLowerCase()) ||
            item[secondaryValueName].toLowerCase().includes(searchtext.toLowerCase())
        );
      } else {
        result = searchlistdata.filter((item) => item[valueName].toLowerCase().includes(searchtext.toLowerCase()));
      }
      setFilteredSFdata(result);
    } else if (searchlistdata) {
      setFilteredSFdata(searchlistdata);
    }
  }, [searchtext, searchlistdata]);

  // SF Input Change
  const handleSFChange = (e) => {
    setEditingSF(true);
    setShowsflist(true);
    setSearchtext(e.target.value);
  };

  const handleSFSelection = (item) => {
    onItemSelect(item);
    setSearchtext(item[valueName]);
    setShowsflist(false);
    // setSearchtext(null);
    setEditingSF(false);
  };

  const handleSFInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
    if (filteredSFdata && filteredSFdata.length > 0 && e.key === "ArrowDown") {
      e.preventDefault();
      SFCurrentIndex = 0;
      // console.log(SFRefs.current[SFCurrentIndex]);
      if (SFCurrentIndex === 0) {
        SFRefs.current[SFCurrentIndex].focus();
      }
      setShowsflist(true);
    }
    if (e.key === "ArrowDown") {
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setSearchtext(null);
      setShowsflist(false);
    }
    if (e.key === "Tab") {
      if (editingSF) {
        e.preventDefault();
      } else {
        setShowsflist(false);
      }
    }
  };

  const handleSFListKeyDown = (e, item) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (SFRefs.current[SFCurrentIndex + 1]) {
        SFCurrentIndex = SFCurrentIndex + 1;
        SFRefs.current[SFCurrentIndex].focus();
      }
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (SFRefs.current[SFCurrentIndex - 1]) {
        SFCurrentIndex = SFCurrentIndex - 1;
        SFRefs.current[SFCurrentIndex].focus();
      }
    }
    if (e.key === "Enter") {
      handleSFSelection(item);
      setShowsflist(false);
    }
    if (e.key === "Tab") {
      if (editingSF) {
        e.preventDefault();
      } else {
        setShowsflist(false);
      }
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setSearchtext(null);
      setShowsflist(false);
    }
  };

  useEffect(() => {
    setSearchtext(null);
    setShowsflist(false);
  }, [clearfield]);

  return (
    <div>
      <input
        required={required}
        autoComplete='off'
        className='sf-input'
        name={valueID}
        style={style}
        value={searchtext !== null ? searchtext : value || ""}
        onChange={(e) => {
          handleSFChange(e);
        }}
        onFocus={(e) => {
          e.target.select();
          loaddata();
        }}
        onKeyDown={handleSFInputKeyDown}
        placeholder={customplaceholder ? customplaceholder : "Select " + valueName}
        ref={(elem) => {
          // console.log(valueID);
          if (location.pathname.includes("positions/usa")) return (inputRefs.current[refName || valueID] = elem);
          if (location.pathname.includes("positions/spain")) return (inputRefsSP.current[refName || valueID] = elem);
          if (location.pathname.includes("qs/usa")) return (inputRefsUSQS.current[refName || valueID] = elem);
          if (location.pathname.includes("qs/spain")) return (inputRefsSPQS.current[refName || valueID] = elem);
        }}
      />
      <ul style={sfliststyle} className={showsflist ? "sf-list" : "hidden"}>
        {filteredSFdata &&
          filteredSFdata.map((item, index) => (
            <p
              key={"SF-" + index}
              ref={(elem) => (SFRefs.current[index] = elem)}
              tabIndex={-1}
              onKeyDown={(e) => handleSFListKeyDown(e, item)}
              onClick={(e) => {
                handleSFSelection(item);
              }}
            >
              {!secondaryValueName ? item[valueName] : item[valueName] + " - " + item[secondaryValueName]}
            </p>
          ))}
      </ul>
    </div>
  );
};

export default SearchField;
